import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  COMMERCE_PAYOR_NOTIFICATION_EMAIL_PREVIEW_QUERY,
  type CommercePayorNotificationEmailPreviewQueryResponse,
  type CommercePayorNotificationEmailPreviewQueryVariables,
  type CommercePayorNotificationEmailType,
  type EmailPreview
} from "../_queries/emailPreviewQueries";

type LoadPreviewArgs = {
  notificationType: CommercePayorNotificationEmailType;
  dueDate?: string | Date | null;
};

export function useCommercePayorNotificationEmailPreview(orderId: string) {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    CommercePayorNotificationEmailPreviewQueryResponse,
    CommercePayorNotificationEmailPreviewQueryVariables
  >(COMMERCE_PAYOR_NOTIFICATION_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async ({ notificationType, dueDate }: LoadPreviewArgs) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          orderId,
          notificationType,
          dueDate: dueDate ?? null
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.commercePayorNotificationEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview, orderId]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
